<template>
  <div class="app-wrapper">
    <!-- 独立的安卓状态栏颜色承载层 -->
    <div class="native-status-bar-holder"></div>

    <!-- 你原有的 #app 保持不变 -->
    <div id="app">
      <!-- 核心：微信环境专属引导图遮罩 -->
      <div v-if="showWechatGuide" class="wechat-guide-mask" @click="closeGuide">
        <!-- 提示箭头 -->
        <div class="guide-arrow"></div>
        <!-- 提示文字 -->
        <div class="guide-content">
          <p>
            点击右上角
            <span style="font-weight: bold; color: #1989fa">···</span>
          </p>
          <p>
            选择
            <span style="font-weight: bold; text-decoration: underline"
              >在浏览器中打开</span
            >
          </p>
          <p style="font-size: 13px; color: #aaa; margin-top: 10px">
            （企业微信用户请选择在 Safari / 系统浏览器打开）
          </p>
        </div>
      </div>

      <!-- 路由缓存控制 -->
      <keep-alive>
        <router-view
          v-if="$route.meta && $route.meta.keepAlive"
          :key="$route.name"
        ></router-view>
      </keep-alive>
      <router-view
        v-if="!($route.meta && $route.meta.keepAlive)"
        :key="$route.fullPath"
      ></router-view>

      <!-- 全局 Loading -->
      <van-overlay :show="loading" :z-index="2000">
        <div class="global-loading">
          <van-loading vertical>加载中...</van-loading>
        </div>
      </van-overlay>
    </div>
  </div>
</template>

<script>
import {
  getQueryParamsFromUrl,
  setLocalStorageData,
  getDeviceOS,
} from "@/common/index";
import { getAdList, getSysConfigByKey } from "@/api/index";
// 引入抽离的公共方法和配置
import {
  checkAndApplyTopOffset,
  setNativeTopInsetPx,
  extractTopInset,
  initInputScrollListener,
  AD_CONFIGS,
  SYS_CONFIGS,
} from "@/hooks/app-helper";
export default {
  name: "App",
  components: {},
  data() {
    return {};
  },
  created() {
    if (process.env.VUE_APP_ENV === "productionApp") {
      this.initAppEnvironment();
    } else {
      this.initH5Environment();
    }
  },

  mounted() {
    initInputScrollListener(); // 外部提取的监听器
    this.fetchGlobalConfigData();
  },
  methods: {
    /**
     * App 壳子环境初始化
     */
    initAppEnvironment() {
      this.$JSBridge.send("serviceType", { type: "1" });

      this.$JSBridge.listen("pushToken", ({ type, data }) => {
        if (type != "1") return;
        this.$store.commit("setToken", data.token);
        this.$store.commit("setUserInfo", { phone: data.phone });
        localStorage.setItem("phone", data.phone);
        this.$router.push({ name: "Home" });
      });

      const homeTopOffset = checkAndApplyTopOffset();
      if (homeTopOffset === 0 && getDeviceOS() === "android") {
        this.initAndroidTopInset();
        this.$JSBridge.send("serviceType", { type: "11" });
      } else if (homeTopOffset > 0) {
        document.documentElement.style.setProperty(
          "--home-top-offset",
          `${homeTopOffset}px`,
        );
      }
    },

    /**
     * 普通 H5 浏览器环境初始化
     */
    initH5Environment() {
      const cachedToken = localStorage.getItem("token") || "";
      if (!this.$store.state.token && cachedToken) {
        this.$store.commit("setToken", cachedToken);
      }
      const queryParams = getQueryParamsFromUrl();
      const routeStrategy = {
        1: () => this.$router.replace({ name: "appAgreement" }),
        2: () => {
          this.$store.commit("setChannelId", queryParams.channelId);
          this.$router.push({ name: "landingPage" });
        },
        4: () => {
           this.$router.replace({
              name: "loadAgreement",
              query: { ageType: queryParams.ageType },
            });
        },
        5: () => {
          this.$store.commit("setToken", queryParams.token);
          this.$router.replace({
            name: "Home",
          });
        },
        6: () => {
          this.$store.commit("setToken", queryParams.token);
          this.$router.replace({
            name: "orderList",
          });
        },
        7: () => {},
      };

      const executeStrategy = routeStrategy[queryParams.type];
      if (executeStrategy) {
        executeStrategy();
      } else if (this.$route.path === "/") {
        this.$router.push({ name: "Login" });
      }
    },

    jumpToLoadAgreement(ageType) {
      this.$router.replace({ name: "loadAgreement", query: { ageType } });
    },

    /**
     * 初始化安卓顶栏安全区监听
     */
    initAndroidTopInset() {
      if (
        process.env.VUE_APP_ENV !== "productionApp" ||
        getDeviceOS() !== "android"
      )
        return;

      const queryParams = getQueryParamsFromUrl();
      const queryInset = extractTopInset({
        top:
          queryParams.safeTop ??
          queryParams.safeAreaTop ??
          queryParams.statusBarHeight,
      });

      if (Number.isFinite(queryInset) && queryInset > 0) {
        setNativeTopInsetPx(queryInset);
      }

      this.$JSBridge.listen("statusBarHeight", (payload) => {
        const inset = extractTopInset(payload);
        if (Number.isFinite(inset) && inset >= 0) {
          setNativeTopInsetPx(inset);
        }
      });
    },

    /**
     * 并发请求广告和全局配置
     */
    async fetchGlobalConfigData() {
      try {
        const [adsResults, configsResults] = await Promise.all([
          Promise.all(
            AD_CONFIGS.map((item) =>
              getAdList({ pageNumber: 1, pageSize: 10, type: item.type }),
            ),
          ),
          Promise.all(SYS_CONFIGS.map((item) => getSysConfigByKey(item.key))),
        ]);

        adsResults.forEach((res, index) => {
          if (res?.code === 2000 && res.data?.recordList?.length) {
            this.$store.commit(
              AD_CONFIGS[index].mutation,
              res.data.recordList[0],
            );
          }
        });

        configsResults.forEach((res, index) => {
          if (res?.code === 2000) {
            setLocalStorageData(
              SYS_CONFIGS[index].localKey,
              res.data.configValue,
            );
          }
        });
      } catch (error) {
        console.error("全局配置数据初始化失败:", error);
      }
    },

    closeGuide() {
      // 如果你允许用户点击遮罩层关闭引导（继续浏览 H5 低功能版），可保留此方法
      // 如果必须强制跳出，请直接注释掉这个方法，让遮罩不可关闭
      this.$store.commit("setShowWechatGuide", false);
      document.body.style.overflow = "";
    },
  },
  computed: {
    loading() {
      return this.$store.state.loading;
    },
    //是否展示过微信引导页面
    showWechatGuide() {
      return this.$store.state.showWechatGuide;
    },
  },
};
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  /* 给 TabBar 留出空间 */
  /* padding-bottom: 60px;  */
}
.global-loading {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
/* 默认隐藏这个占位层 */
.native-status-bar-holder {
  display: none;
}

/* 当处于 App 环境内、且被添加了 .native-top-inset 类名时激活 */
.native-top-inset .native-status-bar-holder {
  display: block;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  /* 完美对齐你之前定义的安卓偏移量 */
  height: var(--app-top-offset, 0px);
  /* 使用动态颜色变量，默认白色 */
  background-color: var(--status-bar-bg, #ffffff) !important;
  /* 确保它在最上层，不会被 H5 内部的其他 fixed 元素遮挡 */
  z-index: 99999;
}
/* 全屏引导遮罩层 */
.wechat-guide-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.85); /* 黑色半透明背景 */
  z-index: 999999; /* 确保压在所有 Vant 组件（包括弹窗、导航栏）的最上方 */
  display: flex;
  flex-direction: column;
  align-items: flex-end; /* 撑到右上角对齐 */
  padding: 20px;
  box-sizing: border-box;
}

/* 右上角提示文字与箭头容器 */
.guide-content {
  text-align: right;
  color: #ffffff;
  font-size: 16px;
  line-height: 1.6;
}

/* 动态闪烁的提示箭头（用 CSS 自带的三角绘制，无需切图） */
.guide-arrow {
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 15px solid #ffffff;
  transform: rotate(45deg); /* 旋转成指向右上角的箭头 */
  margin-right: 15px;
  margin-bottom: 15px;
  animation: bounce 1.5s infinite; /* 增加上下跳动动画，吸引注意 */
}

/* 跳动动画 */
@keyframes bounce {
  0%,
  100% {
    transform: rotate(45deg) translate(0, 0);
  }
  50% {
    transform: rotate(45deg) translate(5px, -5px);
  }
}
</style>
